import {Badge, Box, Text, Button, Flex} from '@chakra-ui/react'
import {map, get, snakeCase} from 'lodash-es'
import React from 'react'
import useSWR from 'swr'
import dayjs from 'dayjs'
import {useLocation, useNavigate} from 'react-router'
import {useTranslation} from 'react-i18next'
import useFeatureFlags from '../../hooks/useFeatureFlags'
import InvitePayerToMarmaladeModal from '../../components/InvitePayerToMarmaladeModal'
import {NewSupplierLayout as SupplierLayout} from '../../layouts/SupplierLayout'
import withProviders from '../../contexts/withProviders'
import withSWR from '../../contexts/withSWR'
import {EmptyList, FilterEmptyList, Pagination} from '../../components'
import DataTable from '../../components/DataTable'
import {useRails} from '../../contexts/rails'
import {fetcher} from '../../../api'
import {camelizeResult} from '../../../api/serializer'
import NotificationBanner from '../../components/NotificationBanner'
import CustomerFilter, {preFilterQuery} from './CustomerFilter'
import {AltCheckIcon, DotIcon} from '../../icons'
import {NextScreenHeader as ScreenHeader} from '../../components/ScreenHeader'

const statementSentValue = (rowData) =>
  rowData.latestStatementSentAt
    ? dayjs(rowData.latestStatementSentAt).format('DD/MM/YYYY')
    : '-'

const overduePattern = /overdue/i
const dueSoonPattern = /due soon/i
const notDueYetPattern = /not due yet/i

const statusDotColor = (status) => {
  if (overduePattern.test(status)) {
    return 'mld.alert.700'
  }
  if (dueSoonPattern.test(status)) {
    return 'mld.warning.700'
  }
  if (notDueYetPattern.test(status)) {
    return 'mld.success.700'
  }
  return 'mld.neutral.500'
}

const statusTextColor = (status) => {
  if (overduePattern.test(status)) {
    return 'mld.alert.700'
  }
  return null
}

const staticColumns = [
  {
    header: 'Name',
    accessorKey: 'contactName',
    cell: ({getValue}) => {
      return <Text className="fs-mask">{getValue()}</Text>
    },
  },
  {
    header: 'No. of unpaid invoices',
    accessorKey: 'outstandingInvoiceCount',
    cell: ({getValue}) => {
      return <Text>{getValue()}</Text>
    },
  },
  {
    header: 'Invoice status',
    accessorKey: 'outstandingInvoiceStatus',
    cell: ({getValue, row}) => {
      const value = getValue()
      return (
        <Flex align="center" gap="2">
          <Badge variant="invoiceStatus">
            {row.original.outstandingInvoiceStatusCount}
          </Badge>
          <Text color={statusTextColor(value)}>{value}</Text>
          <DotIcon boxSize="15px" color={statusDotColor(value)} />
        </Flex>
      )
    },
  },
  {
    header: 'Last statement sent',
    accessorKey: 'latestStatementSentAt',
    accessorFn: statementSentValue,
    cell: ({getValue}) => {
      return <Text>{getValue()}</Text>
    },
  },
  {
    header: 'Statement opened',
    accessorKey: 'latestStatementOpenedAt',
    cell: ({getValue}) => {
      const value = getValue()
      return (
        <Text>
          {value ? dayjs(getValue()).format('DD/MM/YYYY') : '-'}
          {value && <AltCheckIcon ml="xs" />}
        </Text>
      )
    },
  },
]

const viewDetailsColumn = (onViewDetails) => ({
  header: '',
  accessorKey: 'id',
  cell: ({getValue}) => (
    <Button
      variant="secondary"
      onClick={(e) => onViewDetails(getValue(), e)}
      height="8"
      data-testid="view_details_btn"
    >
      View Details
    </Button>
  ),
  enableSorting: false,
})

const SupplierCustomersScreen = () => {
  const {t} = useTranslation('customers')
  const {
    params: preFilterParams,
    activeFilters: preActiveFilters,
    filterOption: preFilterOption,
  } = preFilterQuery(window.location.search)

  const location = useLocation()
  const navigate = useNavigate()
  const isModalOpen = location.pathname.startsWith(
    '/customers/transition_to_marmalade',
  )

  const loadCustomerPage = (customerId) => {
    navigate(`/customers/${customerId}`)
  }

  const customerRowClicked = (customer) => {
    loadCustomerPage(customer.id)
  }

  const viewDetailsClicked = (customerId, event) => {
    event.stopPropagation()
    loadCustomerPage(customerId)
  }

  const columns = [...staticColumns, viewDetailsColumn(viewDetailsClicked)]

  const [filterParams, setFilterParams] = React.useState(preFilterParams || '')
  const [filterOption, setFilterOption] = React.useState(preFilterOption || {})
  const [activeFilters, setActiveFilters] = React.useState(
    preActiveFilters || 0,
  )

  const [sortBy, setSortBy] = React.useState(null)
  const onSort = ([sort]) => {
    if (!sort) {
      setSortBy(null)
    } else {
      setSortBy(`${snakeCase(sort.id)} ${sort.desc ? 'desc' : 'asc'}`)
    }
  }

  const [page, setPage] = React.useState(1)
  const [perPage, setPerPage] = React.useState(20)
  React.useEffect(() => setPage(1), [filterParams, sortBy])

  const {organisation} = useRails()

  const constructUrl = () => {
    let url = `/api/suppliers/${organisation.currentSupplier.id}/customers?page=${page}&per_page=${perPage}`

    if (filterParams) url = `${url}&${filterParams}`
    if (sortBy) url = `${url}&q[s]=${sortBy}`

    return url
  }
  const {data, isLoading} = useSWR(constructUrl(), fetcher, {
    use: [camelizeResult],
  })

  const totalCount = get(data, 'metadata.totalCount', 0)
  const totalPages = get(data, 'metadata.totalPages', 0)

  const customers = map(get(data, 'customers'), (customer) => ({
    id: customer.id,
    contactName: customer.contactName,
    outstandingInvoiceCount: customer.outstandingInvoiceCount,
    outstandingInvoiceStatus: customer.outstandingInvoiceStatus,
    outstandingInvoiceStatusCount: customer.outstandingInvoiceStatusCount,
    latestStatementSentAt: customer.latestStatement?.sentAt,
    latestStatementOpenedAt: customer.latestStatement?.openedAt,
  }))

  const noFilters = activeFilters === 0

  const noData = customers.length === 0

  const emptyList = !isLoading && noData && noFilters

  const filterEmptyList = !isLoading && noData && !noFilters

  const {
    data: {features},
    loading: loadingFeatures,
  } = useFeatureFlags()

  const {data: stepperData, isLoading: loadingStepper} = useSWR(
    `/api/suppliers/${organisation.currentSupplier.id}/cashin_stepper`,
    fetcher,
    {
      use: [camelizeResult],
    },
  )

  const loading = loadingFeatures || loadingStepper

  const displayInvitationModal =
    !loadingFeatures && features?.invitePayersToMarmalade && isModalOpen

  const displayBankingDetailsNotification =
    !loading &&
    features?.invitePayersToMarmalade &&
    stepperData?.stepper?.canTransitionPayersToMarmalade

  return (
    <SupplierLayout state="Customers" variant="secondary">
      <ScreenHeader text="Customers">
        <CustomerFilter
          activeFilters={activeFilters}
          filterOption={filterOption}
          setFilterParams={setFilterParams}
          setFilterOption={setFilterOption}
          setActiveFilters={setActiveFilters}
        />
      </ScreenHeader>

      {!emptyList && displayBankingDetailsNotification && (
        <Box my="sm">
          <NotificationBanner
            status="info"
            title={t('BankingDetailsNotification.customers.title')}
            message={t('BankingDetailsNotification.customers.message')}
            ctaButton={
              <Button
                className="fs-unmask"
                variant="primary"
                onClick={() => {
                  navigate(
                    '/customers/transition_to_marmalade/select_customers',
                    {state: {referrer: location.pathname}},
                  )
                }}
              >
                {t('BankingDetailsNotification.customers.button')}
              </Button>
            }
          />
        </Box>
      )}

      <Box
        p="md"
        borderRadius="lg"
        background="mld.neutral.0"
        flex="auto"
        overflowX="auto"
      >
        <Box className="fs-unmask" whiteSpace="nowrap">
          <DataTable
            data={customers}
            columns={columns}
            enableSorting
            manualSorting
            sortByValue={[{id: 'contactName', desc: false}]}
            isLoading={isLoading}
            onClickRow={customerRowClicked}
            onSort={onSort}
          />

          {emptyList && <EmptyList resourceName="customers" />}
          {filterEmptyList && <FilterEmptyList resourceName="customers" />}
        </Box>

        {totalCount > perPage && (
          <Box mt="sm">
            <Pagination
              totalPages={totalPages}
              currentPage={page}
              totalCount={totalCount}
              setPerPage={setPerPage}
              perPage={perPage}
              onPageChange={setPage}
            />
          </Box>
        )}
      </Box>

      {displayInvitationModal && (
        <InvitePayerToMarmaladeModal isOpen={isModalOpen} />
      )}
    </SupplierLayout>
  )
}

export default withProviders(withSWR(SupplierCustomersScreen))
